import type { EstimatorInput, EstimatorResult } from "@reno-estimator/core";
import type { FieldValidationError, LeadFormRawInput } from "./validation.js";
import { validateLeadForm } from "./validation.js";
import { evaluateSpamSignals, type SpamCheckOptions } from "./spam-protection.js";
import { validatePhotoUploads, type PhotoUploadCandidate } from "./file-handling.js";
import { findRecentDuplicate, buildLeadFingerprint, type DuplicateCheckOptions } from "./duplicate-protection.js";
import { buildLeadRecord } from "./lead-builder.js";
import type { QualificationConfig } from "./qualification.js";
import type { RateLimiter } from "./rate-limiter.js";
import type { LeadCta, LeadRecord, LeadStore } from "./types.js";

export interface SubmitLeadParams {
  store: LeadStore;
  cta: LeadCta;
  input: EstimatorInput;
  result: EstimatorResult;
  rawForm: LeadFormRawInput;
  /** Hidden anti-bot field's value - should be empty for a real user; see spam-protection.ts. */
  honeypotValue?: string;
  /** ISO 8601 timestamp of when the form was first shown, for the spam-protection fill-time check. */
  formRenderedAt: string;
  photos?: PhotoUploadCandidate[];
  source?: string;
  qualificationConfig?: QualificationConfig;
  spamOptions?: SpamCheckOptions;
  duplicateOptions?: DuplicateCheckOptions;
  /**
   * Optional rate limiting, applied before spam/duplicate checks (cheapest
   * rejection first). Deliberately caller-supplied rather than owned
   * internally by this function: a UI layer wires up one shared
   * `RateLimiter` instance across submissions the same way it wires up
   * `LeadStore` (see estimator-app's `leadStore.ts`), so the limit is
   * actually shared across calls rather than reset every time. Omitted
   * entirely (the default), no rate limiting is applied - this keeps the
   * change purely additive for any existing caller/test.
   */
  rateLimiter?: RateLimiter;
  /** Key the rate limiter should track this submission attempt under - typically the normalized contact fingerprint. Required if `rateLimiter` is supplied. */
  rateLimitKey?: string;
  now?: () => Date;
  idFactory?: () => string;
}

export type SubmitLeadOutcome =
  | { status: "invalid"; errors: FieldValidationError[] }
  | { status: "rate_limited" }
  | { status: "rejected_spam"; reasons: string[] }
  | { status: "duplicate"; existingLeadId: string }
  | { status: "saved"; lead: LeadRecord };

/**
 * The single entry point that ties validation, spam protection, duplicate
 * detection, secure photo handling, qualification, and storage together -
 * everything the Phase 16 brief asks for, in one well-defined pipeline so
 * the UI (and tests) never have to reassemble the order of checks
 * themselves. Order matters: a submission is checked for basic validity
 * first (cheapest, most user-actionable), then spam signals, then
 * duplicates, then photos, before anything is persisted.
 */
export async function submitLead(params: SubmitLeadParams): Promise<SubmitLeadOutcome> {
  const formValidation = validateLeadForm(params.rawForm);
  if (!formValidation.valid || !formValidation.value) {
    return { status: "invalid", errors: formValidation.errors };
  }
  const contact = formValidation.value;

  if (params.rateLimiter) {
    const key = params.rateLimitKey ?? `${contact.email?.trim().toLowerCase() ?? ""}:${contact.phone ?? ""}`;
    if (!params.rateLimiter.tryConsume(key)) {
      return { status: "rate_limited" };
    }
  }

  // validateLeadForm only enforces email/phone against the *preferred
  // contact method* - it has no idea which CTA was used. That leaves a
  // real gap: a homeowner can set preferredContactMethod to "phone" (no
  // email captured at all) and still click "Email Me My Estimate," which
  // would otherwise save and confirm successfully with no address to
  // actually send anything to. The email CTA has its own hard
  // requirement independent of the preference above.
  if (params.cta === "email_me_my_estimate" && !contact.email) {
    return {
      status: "invalid",
      errors: [{ field: "email", code: "email_required_for_email_cta", message: "Enter an email address so we can send you the estimate." }]
    };
  }

  const now = params.now ?? (() => new Date());
  const spamResult = evaluateSpamSignals(
    {
      honeypotValue: params.honeypotValue,
      formRenderedAt: params.formRenderedAt,
      submittedAt: now().toISOString(),
      name: contact.name,
      email: contact.email,
      projectDescription: contact.projectDescription
    },
    params.spamOptions
  );
  if (spamResult.isLikelySpam) {
    return { status: "rejected_spam", reasons: spamResult.reasons };
  }

  const fingerprint = buildLeadFingerprint(contact, params.input.projectType);
  const existingLeads = await params.store.list();
  const duplicate = findRecentDuplicate(existingLeads, fingerprint, { ...params.duplicateOptions, now });
  if (duplicate) {
    return { status: "duplicate", existingLeadId: duplicate.id };
  }

  let photoDescriptors: LeadRecord["photos"] = [];
  if (params.photos && params.photos.length > 0) {
    const photoValidation = validatePhotoUploads(params.photos);
    if (!photoValidation.valid) {
      return {
        status: "invalid",
        errors: photoValidation.errors.map((e) => ({ field: "photos", code: e.code, message: e.message }))
      };
    }
    photoDescriptors = photoValidation.descriptors;
  }

  const lead = buildLeadRecord({
    cta: params.cta,
    input: params.input,
    result: params.result,
    contact,
    photos: photoDescriptors,
    source: params.source,
    qualificationConfig: params.qualificationConfig,
    now,
    idFactory: params.idFactory
  });

  await params.store.save(lead);
  return { status: "saved", lead };
}
